import { useEffect, useState } from 'react';
import {
  identityApiRef,
  IdentityApi,
  useApi,
} from '@backstage/frontend-plugin-api';
import {
  Account,
  AccountEntity,
  AccountUserRole,
} from '@internal/backstage-plugin-catalog-devops-common';

/**
 * Maps an account entity to the `Account` payload the edit endpoint accepts.
 * Shared by the settings page and any component that updates a single
 * aspect of the account (e.g. muting scan errors) so no field is lost;
 * edited form values are passed as overrides.
 */
export function buildAccountPayload(
  account: AccountEntity,
  overrides: Partial<Account> = {},
): Account {
  return {
    AccountId: account.metadata.name,
    AccountName: account.metadata.title ?? '',
    Title: account.metadata.title ?? '',
    Type: account.spec.type as Account['Type'],
    Description: account.metadata.description ?? '',
    BackstageOwner: account.spec.owner,
    Project: account.spec.domain,
    Labels: Object.entries(account.metadata.labels ?? {}).map(
      ([key, value]) => ({ key, value }),
    ),
    Errors: account.spec.errors ?? [],
    MutedErrors: account.spec.mutedErrors ?? [],
    Regions: account.spec.regions ?? [],
    ReportFormatVersion: account.spec.reportFormatVersion ?? 0,
    InventoryFormatVersion: account.spec.inventoryFormatVersion ?? 0,
    CatalogFormatVersion: account.spec.catalogFormatVersion ?? 0,
    LastScanTime: account.spec.lastScanTime,
    LastSyncTime: account.spec.lastSyncTime,
    AuthType: account.spec.authType,
    ScanSchedule: account.spec.scanSchedule,
    Users: account.spec.users ?? [],
    ExpectedUpdatedAt: account.spec.updatedAt,
    ...overrides,
  };
}

/**
 * Reduces an entity ref (e.g. `user:default/jdoe`, `user/jdoe`, `jdoe`)
 * to the lowercase bare entity name so refs from different sources can
 * be compared safely.
 */
export function normalizeEntityRef(entityRef: string | undefined): string {
  if (!entityRef) {
    return '';
  }

  let name = entityRef.trim().toLowerCase();
  const namespaceSeparator = name.indexOf(':');
  if (namespaceSeparator !== -1) {
    name = name.slice(namespaceSeparator + 1);
  }
  const kindSeparator = name.indexOf('/');
  if (kindSeparator !== -1) {
    name = name.slice(kindSeparator + 1);
  }
  return name;
}

export function getAccountUserRole(
  account: AccountEntity | undefined,
  userRef: string | undefined,
): AccountUserRole | undefined {
  const normalizedUserRef = normalizeEntityRef(userRef);
  if (!account || !normalizedUserRef) {
    return undefined;
  }

  return account.spec.users?.find(
    user => normalizeEntityRef(user.UserRef) === normalizedUserRef,
  )?.Role;
}

export function useCurrentUserRef(): string | undefined {
  const identityApi = useApi<IdentityApi>(identityApiRef);
  const [userRef, setUserRef] = useState<string | undefined>(undefined);

  useEffect(() => {
    let ignore = false;

    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (!ignore) {
          setUserRef(identity.userEntityRef);
        }
      })
      .catch(() => {
        if (!ignore) {
          setUserRef(undefined);
        }
      });

    return () => {
      ignore = true;
    };
  }, [identityApi]);

  return userRef;
}

export function useIsAccountAdmin(account: AccountEntity | undefined): boolean {
  const currentUserRef = useCurrentUserRef();
  return getAccountUserRole(account, currentUserRef) === AccountUserRole.ADMIN;
}
